import React, { useState, useEffect } from "react"
import { invitationsApi } from "../../lib/api"
import { useToast } from "../../contexts/ToastContext"

const InvitationsList = ({ refreshTrigger }) => {
  const [invitations, setInvitations] = useState([])
  const [loading, setLoading] = useState(true)
  const [copiedId, setCopiedId] = useState(null)
  const toast = useToast()

  useEffect(() => {
    fetchInvitations()
  }, [refreshTrigger])

  const fetchInvitations = async () => {
    try {
      setLoading(true)
      const data = await invitationsApi.list()
      setInvitations(data.invitations || [])
    } catch (err) {
      console.error("Failed to fetch invitations:", err)
    } finally {
      setLoading(false)
    }
  }

  const handleRevoke = async (id) => {
    if (!confirm("Are you sure you want to revoke this invitation?")) return

    try {
      await invitationsApi.revoke(id)
      toast.success("Invitation revoked")
      fetchInvitations()
    } catch (err) {
      toast.error(err.message || "Failed to revoke invitation")
    }
  }

  const handleCopyLink = async (token, id) => {
    const link = `${window.location.origin}/accept-invitation/${token}`
    try {
      await navigator.clipboard.writeText(link)
      setCopiedId(id)
      toast.success("Link copied to clipboard!")
      setTimeout(() => setCopiedId(null), 2000)
    } catch (err) {
      toast.error("Failed to copy link")
    }
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })
  }

  const getStatusBadge = (invitation) => {
    if (invitation.status === "accepted") {
      return (
        <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
          Accepted
        </span>
      )
    }
    if (invitation.expired || invitation.status === "expired") {
      return (
        <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
          Expired
        </span>
      )
    }
    if (invitation.status === "revoked") {
      return (
        <span className="px-2 py-1 bg-red-100 text-red-600 text-xs rounded-full">
          Revoked
        </span>
      )
    }
    return (
      <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full">
        Pending
      </span>
    )
  }

  if (loading) {
    return (
      <div className="animate-pulse space-y-3">
        <div className="h-16 bg-gray-200 rounded-lg"></div>
        <div className="h-16 bg-gray-200 rounded-lg"></div>
      </div>
    )
  }

  if (invitations.length === 0) {
    return (
      <p className="text-gray-500 text-sm text-center py-4">
        No invitations sent yet.
      </p>
    )
  }

  return (
    <div className="space-y-3">
      {invitations.map((invitation) => (
        <div
          key={invitation.id}
          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
        >
          <div className="min-w-0 flex-1">
            <p className="font-medium text-gray-900 truncate">{invitation.parent_email}</p>
            <p className="text-sm text-gray-500">
              {invitation.status === "accepted"
                ? `Accepted on ${formatDate(invitation.accepted_at)}`
                : `Sent on ${formatDate(invitation.created_at)}`}
              {invitation.status === "pending" && !invitation.expired && (
                <span className="text-gray-400">
                  {" "}· Expires {formatDate(invitation.expires_at)}
                </span>
              )}
            </p>
          </div>

          <div className="flex items-center gap-2">
            {getStatusBadge(invitation)}

            {invitation.status === "pending" && !invitation.expired && (
              <>
                <button
                  onClick={() => handleCopyLink(invitation.token, invitation.id)}
                  className={`text-sm px-2 py-1 rounded transition-colors ${
                    copiedId === invitation.id
                      ? "text-green-600 bg-green-50"
                      : "text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50"
                  }`}
                  title="Copy invitation link"
                >
                  {copiedId === invitation.id ? "Copied!" : "Copy Link"}
                </button>
                <button
                  onClick={() => handleRevoke(invitation.id)}
                  className="text-sm text-red-600 hover:text-red-800 px-2 py-1"
                >
                  Revoke
                </button>
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

export default InvitationsList
